import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {NgForm} from "@angular/forms";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {TerminalsImportRow} from "../../../../shared/interface/terminals-import-row";
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../../shared/interface/user-permissions";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthService} from "../../../../shared/services/auth.service";
import {FilterService} from "../../../../shared/services/filter.service";
import {ActivatedRoute, Router} from "@angular/router";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../../shared/ngrx/reducers";
import {TerminalsImportRowService} from "../../../../shared/services/terminals-import-row.service";
import {delay, distinctUntilChanged, first, takeUntil} from "rxjs/operators";
import {CountedAndPaginated} from "../../../../shared/interface/response/counted-and-paginated";
import {HttpErrorResponse} from "@angular/common/http";
import {getCurrentRouteState} from "../../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../../shared/ngrx/route-serializer";
import {Uuid} from "../../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../../shared/components/job-result-dialog/job-result-dialog.component";
import {
  ChangeRegistrationDialogComponent
} from "../../../receivables/imports-row/change-registration-dialog/change-registration-dialog.component";
import {
  ChangePartnerFacilityDialogComponent
} from "../change-partner-facility-dialog/change-partner-facility-dialog.component";
import {
    AGENT_21_STATUS_APPLICATION_FOR_RENEWAL, AGENT_21_STATUS_CANCELLATION_OF_RENEWAL,
    AGENT_21_STATUS_IT_EXPIRES, TERMINALS_IMPORT_ROW_STATUS_FOR_VERIFICATION
} from "../../../../shared/services/dict.service";

@Component({
  selector: 'app-all-terminals-imports-row-list',
  templateUrl: './all-terminals-imports-row-list.component.html',
  styleUrls: ['./all-terminals-imports-row-list.component.scss']
})
export class AllTerminalsImportsRowListComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() statusKey?: string[] = null;
  readonly TERMINALS_IMPORT_ROW_STATUS_FOR_VERIFICATION = TERMINALS_IMPORT_ROW_STATUS_FOR_VERIFICATION;

  initialFilters: any;
  rows: TerminalsImportRow[] = [];
  displayedColumns: string[] = ['id' , 'partner', 'facility', 'transactionAt', 'paymentTitle' , 'transferAmount' , 'status', 'action'];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
    private terminalsImportRowService: TerminalsImportRowService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.terminalsImportRowService.rowListSubject
      .pipe(
        takeUntil(this.destroyed$),
        delay(0)
      ).subscribe({
      next: (response: CountedAndPaginated<TerminalsImportRow>) => {
        this.rows = response.items;
        this.totalData = response.pagination.total;
      },
      error: (response: HttpErrorResponse) => {
        this.snack.open('Przy ładowaniu danych wystąpił błąd', response.status.toString(), {duration: 2000});
      }
    });
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      partner: 'string',
      gte$transferAmount: 'string',
      lte$transferAmount: 'string',
      lte$transactionAt: 'string',
      gte$transactionAt: 'string',
      haveFacility: 'int',
      haveTerminal: 'int',
    }, filters);
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.statusKey = this.statusKey ?? data.statusKey ?? null;
        this.store
          .pipe(
            select(getCurrentRouteState),
            delay(0),
            distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
            takeUntil(this.destroyed$),
          )
          .subscribe((state: RouterStateUrl) => {
            this.parseFilters(state.params);
            this.paginator.pageIndex = state.params.page;
            this.paginator.pageSize = state.params.pageSize;
            this.loadListData();
          });
      });
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (null !== this.statusKey) {
      filters.statusKey = this.statusKey;
    }

    return filters;
  }

  async loadListData(): Promise<void> {
    this.terminalsImportRowService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters());
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  downloadReport(): void {
    this.terminalsImportRowService.downloadReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  partnerFacilityChange(terminalsImportRow: TerminalsImportRow): void {
    const dialogRef = this.dialog.open(ChangePartnerFacilityDialogComponent, {
      width: '700px',
      data: {
        terminalsObject: terminalsImportRow
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadListData();
    });
  }

}
